/**
 * The new-user lifecycle email sequence — what may be sent, and when.
 *
 * Lives in shared rather than behind an endpoint for the same reason
 * `starters.ts` does: the operator dashboard renders one label per step and
 * the sweep sends against the same list, so a second source for the ids would
 * be free to disagree with what actually went out.
 *
 * Browser-safe by construction — data and two pure predicates, no clock of its
 * own and nothing Node-only. The caller supplies `now`.
 *
 * Vocabulary: this is user-facing machinery, so the copy these ids drive says
 * *poll* and *vote*. The ids themselves are internal and never rendered.
 */

/** The four scheduled steps, plus the welcome. Ordered by `dayOffset`. */
export type LifecycleStepId = 'welcome' | 'first-poll' | 'present-it' | 'what-you-keep' | 'marquee';

/**
 * The one email that is not on a schedule: it answers something that happened
 * in a room. Kept out of `LifecycleStepId` because the sweep treats it
 * differently at every turn — different claim key, different recipient set,
 * and it recurs where the scheduled steps are one-shot.
 */
export const ROOM_FILLING_ID = 'room-filling';

/** Every id that can appear in a send log or on the operator dashboard. */
export type LifecycleEmailId = LifecycleStepId | typeof ROOM_FILLING_ID;

/**
 * Whether a step is commercial speech.
 *
 * Load-bearing rather than descriptive: `commercial` steps are the ones the
 * plan gates on the workspace being free and the recipient being an admin, and
 * the ones that may not send without a postal address configured.
 */
export type LifecycleKind = 'onboarding' | 'commercial';

export interface LifecycleStep {
  id: LifecycleStepId;
  /** Days after `createdAt` that this step becomes due. */
  dayOffset: number;
  kind: LifecycleKind;
  /** Shown on the operator dashboard, never to the recipient. */
  label: string;
}

export const LIFECYCLE_STEPS: readonly LifecycleStep[] = [
  { id: 'welcome', dayOffset: 0, kind: 'onboarding', label: 'Welcome' },
  { id: 'first-poll', dayOffset: 2, kind: 'onboarding', label: 'First poll' },
  { id: 'present-it', dayOffset: 5, kind: 'onboarding', label: 'Present it' },
  { id: 'what-you-keep', dayOffset: 10, kind: 'commercial', label: 'What you keep' },
  { id: 'marquee', dayOffset: 21, kind: 'commercial', label: 'Marquee' },
] as const;

export const LIFECYCLE_EMAIL_IDS: readonly LifecycleEmailId[] = [
  ...LIFECYCLE_STEPS.map((step) => step.id),
  ROOM_FILLING_ID,
];

export const LIFECYCLE_LABELS: Record<LifecycleEmailId, string> = {
  ...(Object.fromEntries(LIFECYCLE_STEPS.map((step) => [step.id, step.label])) as Record<
    LifecycleStepId,
    string
  >),
  [ROOM_FILLING_ID]: 'Room filling up',
};

export const isLifecycleEmailId = (value: string): value is LifecycleEmailId =>
  (LIFECYCLE_EMAIL_IDS as readonly string[]).includes(value);

/**
 * No account created before this instant is ever enrolled.
 *
 * The single most dangerous thing about a sweep over `ENTITY#user#<YYYY-MM>`
 * is that the partition holds the entire back catalogue. Any off-by-one in the
 * window, and every user who ever signed up is mailed at once. This is the
 * floor that makes that impossible, and it is checked in `nextStepFor` rather
 * than only in the query, so no future caller can route around it.
 *
 * Deliberately the same day as `TERMS_EFFECTIVE_DATE`, because that is the day
 * the privacy policy first described these emails. The first deploy therefore
 * enrols nobody and the campaign fills up from new signups. **Moving this
 * backwards mails people who signed up before the policy described this.**
 *
 * Leaving it *behind* a later deploy is the quieter mistake: accounts created
 * between this date and the deploy are past the epoch and inside the 35-day
 * window, so they receive the sequence at one-a-day the moment they accept the
 * bumped terms rather than on its intended cadence. Move this to the deploy
 * date, in the same commit as the deploy.
 *
 * **Set to 29 Aug 2026, the day this commit was prepared — not necessarily the
 * day it deploys.** Prod only moves on a published release, and the release
 * carrying this may land later than the merge that adds it. If it does,
 * **bump this again to the actual publish date before that release goes out**;
 * per the paragraph above, being early is the dangerous direction, being late
 * is only ever the quieter mistake.
 */
export const LIFECYCLE_EPOCH = '2026-08-29T00:00:00.000Z';

/** The last scheduled step's offset — how long the sequence runs. */
export const LIFECYCLE_WINDOW_DAYS = 21;

/**
 * How far past its due day a step may still be sent.
 *
 * A sweep that fails, or an account that sat unaccepted for a fortnight and
 * then accepted, should still get the sequence rather than silently skipping
 * it. Past this, the step is dropped: a "create your first poll" note two
 * months after signup reads as a system that lost track of you.
 */
export const LIFECYCLE_CATCHUP_DAYS = 14;

/** Users older than this are outside the sequence entirely, and are not read. */
export const LIFECYCLE_MAX_AGE_DAYS = LIFECYCLE_WINDOW_DAYS + LIFECYCLE_CATCHUP_DAYS;

/**
 * The participant count that earns the `room-filling` email, against
 * `FREE_AUDIENCE_CAP` of 100.
 *
 * Deliberately below the cap rather than at it. A presenter whose room was
 * turned away has already had the bad experience; one whose room came close
 * has not, and telling them before the next event is the difference between a
 * useful warning and an apology.
 */
export const ROOM_FILLING_THRESHOLD = 80;

/** Whole days from `createdAt` to `now`, floored. Negative clocks read as 0. */
export function ageInDays(createdAt: string, now: Date): number {
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return -1;
  return Math.max(0, Math.floor((now.getTime() - created) / 86_400_000));
}

/** Whether an account is young enough to still be in the sequence. */
export function withinCampaignWindow(createdAt: string, now: Date): boolean {
  if (createdAt < LIFECYCLE_EPOCH) return false;
  const age = ageInDays(createdAt, now);
  return age >= 0 && age <= LIFECYCLE_MAX_AGE_DAYS;
}

/**
 * What the operator dashboard shows about the campaign.
 *
 * Aggregates only. There is no shape here that could answer "which emails did
 * this person get" — the send claims that could are in users' own partitions
 * and are never enumerated, which is the same aggregate-only stance
 * `shared/usage.ts` takes about console events.
 */
export interface LifecycleOverview {
  totals: { sent: number; optOuts: number; failures: number };
  byStep: { id: LifecycleEmailId; label: string; sent: number }[];
  days: { day: string; sent: number; optOuts: number; failures: number }[];
}
